import { motion } from "framer-motion";
import { MapPin, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuote } from "@/context/QuoteContext";
import { SITE, IMAGES } from "@/data/site";

const NORCAL = [
  "San Francisco", "Oakland", "San Jose", "San Rafael", "Richmond", "Berkeley",
  "Concord", "Walnut Creek", "Daly City", "San Mateo", "Redwood City", "Palo Alto",
  "Mountain View", "Sunnyvale", "Santa Clara", "Milpitas", "Fremont", "Pleasanton",
  "Livermore", "Santa Cruz", "Monterey", "Carmel-by-the-Sea", "Morro Bay",
];
const SOCAL = [
  "Ventura", "Glendale", "Burbank", "Hollywood", "Los Angeles", "Pasadena",
  "Altadena", "Beverly Hills", "Santa Monica", "Thousand Oaks", "Culver City",
  "Oxnard", "Simi Valley",
];

export const ServiceAreas = () => {
  const { openQuote } = useQuote();

  return (
    <section id="areas" data-testid="areas-section" className="relative py-16 sm:py-20 lg:py-28 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.commercial} alt="California construction skyline" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/90" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-red-600">Where We Work</span>
          <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
            Proudly Serving All of {SITE.areas}
          </h2>
          <p className="mt-4 text-lg text-slate-300 leading-relaxed">
            From the Bay Area, Santa Cruz, the Monterey Peninsula and the Central Coast, all the way down to
            Greater Los Angeles, if you're in California, Templar Armor has you covered. Residential, HOA, casino
            and commercial.
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            data-testid="coverage-map"
            className="rounded-2xl overflow-hidden bg-white shadow-2xl border border-white/10"
          >
            <img src={IMAGES.coverageMap} alt="Templar Armor Northern California coverage area map" className="w-full h-auto" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            data-testid="coverage-map-la"
            className="rounded-2xl overflow-hidden bg-white shadow-2xl border border-white/10"
          >
            <img src={IMAGES.coverageMapLA} alt="Templar Armor Greater Los Angeles coverage area map" className="w-full h-auto" />
          </motion.div>
        </div>

        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          {[{ title: "Northern California, Bay Area & Central Coast", list: NORCAL }, { title: "Greater Los Angeles", list: SOCAL }].map((region, i) => (
            <motion.div
              key={region.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`region-${i}`}
              className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur-sm"
            >
              <h3 className="flex items-center gap-2 font-display font-bold text-xl text-white">
                <MapPin className="h-5 w-5 text-red-600" /> {region.title}
              </h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {region.list.map((city) => (
                  <span key={city} className="text-sm font-medium text-slate-200 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                    {city}
                  </span>
                ))}
                <span className="text-sm font-medium text-red-400 bg-red-600/10 border border-red-600/20 rounded-full px-3 py-1">
                  & surrounding areas
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Button
            data-testid="areas-quote-btn"
            onClick={() => openQuote()}
            size="lg"
            className="bg-red-700 hover:bg-red-800 text-white font-bold rounded-full h-14 px-8 hover:scale-105 transition-transform"
          >
            Check My Area, Get a Quote <ArrowRight className="ml-1 h-5 w-5" />
          </Button>
          <a href={SITE.phoneHref} data-testid="areas-call-btn">
            <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 bg-white/10 text-white hover:bg-white hover:text-slate-900 font-bold rounded-full h-14 px-8">
              <Phone className="mr-1 h-5 w-5" /> Call {SITE.phone}
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};
